import React from "react";
import {
  ButtonBase,
  ButtonProps,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { muiTheme } from "@/styles/theme";

export interface SquareButtonProps extends Omit<ButtonProps, "variant"> {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  variant?: "ghost" | "outlined" | "contained" | "ghost-black";
  children?: React.ReactNode;
  title?: string;
  isLoading?: boolean;
}

const SquareButton = ({
  onClick,
  variant = "ghost",
  children,
  title,
  sx,
  isLoading,
  disabled,
}: SquareButtonProps) => {
  let color = muiTheme.colorSchemes.light.palette.primary[500];
  let background;
  let border;
  let hoverColor;
  let hoverBackground;
  const theme = useTheme();

  const disabledColor = theme.nawy.colors.semantic.text.extension.disabled;
  const disabledBackground = theme.nawy.colors.primitives.grey[200];

  switch (variant) {
    case "ghost":
      color = muiTheme.colorSchemes.light.palette.primary[500];
      background = "transparent";
      border = 0;
      hoverColor = muiTheme.colorSchemes.light.palette.primary[500];
      hoverBackground = theme.nawy.colors.primitives.grey[200];
      break;
    case "outlined":
      color = muiTheme.colorSchemes.light.palette.primary[500];
      background = "white";
      border = 1;
      hoverColor = muiTheme.colorSchemes.light.palette.primary[500];
      hoverBackground = theme.nawy.colors.primitives.grey[100];
      break;
    case "contained":
      color = "white";
      background = muiTheme.colorSchemes.light.palette.primary[500];
      border = 0;
      hoverColor = "white";
      hoverBackground = muiTheme.colorSchemes.light.palette.primary[600];
      break;
    case "ghost-black":
      color = "black";
      background = "transparent";
      border = 0;
      hoverColor = muiTheme.colorSchemes.light.palette.primary[500];
      hoverBackground = theme.nawy.colors.primitives.grey[200];
      break;
  }

  const button = () => {
    return (
      <ButtonBase
        disabled={isLoading || disabled}
        onClick={onClick}
        sx={{
          width: "36px",
          height: "36px",
          borderRadius: "8px",
          background,
          border,
          borderColor: theme.nawy.colors.primitives.grey[300],
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color,
          "&:hover": {
            background: hoverBackground,
            color: hoverColor,
          },
          "&:disabled": {
            background: disabledBackground,
            color: disabledColor,
          },
          ...sx,
        }}
        disableRipple={false} // explicitly enable ripple if needed
      >
        {isLoading ? <CircularProgress size={20} /> : <>{children}</>}
      </ButtonBase>
    );
  };
  if (title) {
    return (
      <Tooltip title={title} arrow>
        {button()}
      </Tooltip>
    );
  }

  return button();
};

export default SquareButton;
